import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  HStack,
  useToast,
  Spinner,
  Center,
  Progress,
  Flex,
  ProgressLabel,
  Tooltip,
} from "@chakra-ui/react";
import { getBackendURL } from "./env";

function DiskIOMonitor() {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/function-data-monitor/`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Error fetching Disk IO data:", err);
      setError(err.message || "Failed to fetch Disk IO data");
      toast({
        title: "Error",
        description: err.message || "Failed to fetch Disk IO data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let unitIndex = 0;
    let value = bytes;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    return `${value.toFixed(2)} ${units[unitIndex]}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "N/A";
    return num.toLocaleString();
  };

  const getHitRatioColor = (ratio) => {
    if (ratio >= 95) return "green";
    if (ratio >= 80) return "orange";
    return "red";
  };

  if (isLoading && !data) {
    return (
      <Center py={4}>
        <Spinner size="md" />
      </Center>
    );
  }

  const diskIOData = data?.disk_io_bandwidth || [];
  const tableIOData = data?.table_io_summary || [];
  const dbIOSummary = data?.database_io_summary || {};

  // Calculate totals
  const totalReads = diskIOData.reduce(
    (sum, t) => sum + (t.total_read || 0),
    0,
  );
  const totalHits = diskIOData.reduce((sum, t) => sum + (t.total_hit || 0), 0);
  const overallHitRatio =
    totalReads + totalHits > 0
      ? (totalHits / (totalReads + totalHits)) * 100
      : 100;
  const totalIOMB = diskIOData.reduce(
    (sum, t) => sum + (t.io_bandwidth_mb || 0),
    0,
  );

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between">
          <Heading size="md">Disk IO Bandwidth</Heading>
          <Badge
            colorScheme={
              overallHitRatio >= 80
                ? "green"
                : overallHitRatio >= 60
                  ? "orange"
                  : "red"
            }
            fontSize="md"
          >
            {overallHitRatio.toFixed(1)}% hit ratio
          </Badge>
        </HStack>
      </CardHeader>
      <CardBody>
        {/* Database IO Summary */}
        {dbIOSummary && Object.keys(dbIOSummary).length > 0 && (
          <Box mb={4} p={4} bg="blue.50" borderRadius="md">
            <Heading size="sm" mb={3}>
              Database IO Summary
            </Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Heap Reads</StatLabel>
                <StatNumber fontSize="md">
                  {formatNumber(dbIOSummary.total_heap_read || 0)}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Heap Hits</StatLabel>
                <StatNumber fontSize="md" color="green.500">
                  {formatNumber(dbIOSummary.total_heap_hit || 0)}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Total IO</StatLabel>
                <StatNumber fontSize="md" color="blue.500">
                  {formatBytes(
                    (dbIOSummary.io_bandwidth_mb || 0) * 1024 * 1024,
                  )}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Cache Ratio</StatLabel>
                <StatNumber
                  fontSize="md"
                  color={`${getHitRatioColor(dbIOSummary.cache_hit_ratio || 0)}.500`}
                >
                  {(dbIOSummary.cache_hit_ratio || 0).toFixed(1)}%
                </StatNumber>
              </Stat>
            </SimpleGrid>
          </Box>
        )}

        {/* Overall Stats */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
          <Stat>
            <StatLabel>Total Tables</StatLabel>
            <StatNumber>{diskIOData.length}</StatNumber>
            <StatHelpText>With IO activity</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Total Blocks Read</StatLabel>
            <StatNumber color="blue.500">{formatNumber(totalReads)}</StatNumber>
            <StatHelpText>Disk reads</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Total Blocks Hit</StatLabel>
            <StatNumber color="green.500">{formatNumber(totalHits)}</StatNumber>
            <StatHelpText>Cache hits</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Total IO Bandwidth</StatLabel>
            <StatNumber color="purple.500">
              {totalIOMB.toFixed(2)} MB
            </StatNumber>
            <StatHelpText>Approx. disk IO</StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Disk IO Bandwidth by Table */}
        {diskIOData.length > 0 ? (
          <>
            <Heading size="sm" mb={3}>
              Tables by IO Bandwidth (What consumes most resources)
            </Heading>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Table</Th>
                    <Th isNumeric>Heap Read</Th>
                    <Th isNumeric>Heap Hit</Th>
                    <Th isNumeric>Idx Read</Th>
                    <Th isNumeric>Idx Hit</Th>
                    <Th isNumeric>Hit Ratio</Th>
                    <Th isNumeric>IO (MB)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {diskIOData.map((table, idx) => (
                    <Tr
                      key={idx}
                      bg={
                        table.io_bandwidth_mb > 100
                          ? "red.50"
                          : table.io_bandwidth_mb > 50
                            ? "orange.50"
                            : "inherit"
                      }
                    >
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {table.relation_name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {table.schema_name}
                          </Text>
                        </VStack>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="red.500">
                          {formatNumber(table.heap_read)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="green.500">
                          {formatNumber(table.heap_hit)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm">
                          {formatNumber(table.idx_read)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="green.500">
                          {formatNumber(table.idx_hit)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Tooltip
                          label={`${table.hit_ratio?.toFixed(1) || 0}% cache hits`}
                        >
                          <Badge
                            colorScheme={getHitRatioColor(table.hit_ratio || 0)}
                          >
                            {table.hit_ratio?.toFixed(1) || 0}%
                          </Badge>
                        </Tooltip>
                      </Td>
                      <Td isNumeric>
                        <Text
                          fontSize="sm"
                          fontWeight="bold"
                          color={
                            table.io_bandwidth_mb > 100
                              ? "red.500"
                              : table.io_bandwidth_mb > 50
                                ? "orange.500"
                                : "inherit"
                          }
                        >
                          {table.io_bandwidth_mb?.toFixed(2) || 0} MB
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </>
        ) : (
          <Text color="gray.500" textAlign="center" py={4}>
            No Disk IO data available. Run the SQL to add monitoring functions.
          </Text>
        )}

        {/* Table IO Summary - Operation Types */}
        {tableIOData.length > 0 && (
          <>
            <Heading size="sm" mt={6} mb={3}>
              Table Operations Summary
            </Heading>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Table</Th>
                    <Th isNumeric>Total Reads</Th>
                    <Th isNumeric>Total Writes</Th>
                    <Th isNumeric>IO (MB)</Th>
                    <Th>Operation Type</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tableIOData.slice(0, 15).map((table, idx) => (
                    <Tr key={idx}>
                      <Td>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {table.table_name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {table.schema_name}
                          </Text>
                        </VStack>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="blue.500">
                          {formatNumber(table.total_reads)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color="green.500">
                          {formatNumber(table.total_writes)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" fontWeight="medium">
                          {table.total_io_mb?.toFixed(2) || 0} MB
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            table.operation_type === "Heavy Reads"
                              ? "blue"
                              : table.operation_type === "Heavy Writes"
                                ? "green"
                                : "purple"
                          }
                        >
                          {table.operation_type}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default DiskIOMonitor;
